import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes, useState } from 'react';
import { cn } from '../lib/utils.js';

/* Avatar auto-contido (sem Radix): imagem opcional com fallback para iniciais. Tom
   neutro por padrão; `tone="primary"` distingue o interlocutor (ex.: staff do suporte). */
const avatarVariants = cva(
  'inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full font-medium leading-none',
  {
    variants: {
      size: {
        sm: 'size-7 text-[11px]',
        md: 'size-9 text-sm',
        lg: 'size-11 text-base',
      },
      tone: {
        neutral: 'bg-muted text-muted-foreground',
        primary: 'bg-primary/12 text-primary',
      },
    },
    defaultVariants: { size: 'md', tone: 'neutral' },
  },
);

interface AvatarProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof avatarVariants> {
  readonly name: string;
  readonly src?: string;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0]?.[0] ?? '?').toUpperCase();
  return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase();
}

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ name, src, size, tone, className, ...props }, ref) => {
    const [failed, setFailed] = useState(false);
    const showImg = src !== undefined && src.length > 0 && !failed;
    return (
      <div
        ref={ref}
        role="img"
        aria-label={name}
        className={cn(avatarVariants({ size, tone }), className)}
        {...props}
      >
        {showImg ? (
          <img
            src={src}
            alt=""
            className="size-full object-cover"
            onError={() => setFailed(true)}
          />
        ) : (
          <span aria-hidden="true">{initials(name)}</span>
        )}
      </div>
    );
  },
);
Avatar.displayName = 'Avatar';

export { avatarVariants };
